import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getDataFromRelatedFeatures } from "../utils/getDataFromRelatedFeatures";
import { getDataFromAttributes } from "../utils/getDataFromAttributes";
import { getReferenceLayerInfos } from "../../../ducks/showFeature/selectors";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { FeatureCardChartProps } from "../types";
import { ChartDataProps } from "../components/types";
import { LayerInfo } from "../../../interfaces/resourceInfo";

export const useChartData = ({ config, element, attributes, type }: Omit<FeatureCardChartProps, "renderElement">) => {
  const { dataSources } = useWidgetContext(type);
  const layerInfos = useSelector(getReferenceLayerInfos);

  const relatedAttributes = element?.options?.relatedDataSources || [];

  const loading = useMemo(
    () =>
      !!relatedAttributes?.length &&
      !dataSources?.some(({ name }) =>
        relatedAttributes.some(({ chartAxis, dataSourceName }) => chartAxis === "y" && dataSourceName === name),
      ),
    [dataSources, relatedAttributes],
  );

  const fetchedData = useMemo(() => {
    const configDataSources = config?.dataSources || [];
    const isRelated = !!relatedAttributes?.length;
    const filteredAttributes = relatedAttributes.filter(({ chartAxis }) => chartAxis === "y");

    if (!element) return [];

    if (isRelated) {
      if (!filteredAttributes.length) return [];

      return filteredAttributes
        .map(relatedAxis => {
          const { dataSourceName, axisColor, attributeName } = relatedAxis;
          const dataSource = configDataSources.find(({ name }) => name === dataSourceName);
          const layerInfo = layerInfos?.find(item => item?.name === dataSource?.layerName);
          const featureDataSource = dataSources?.find(({ name }) => name === dataSourceName);
          const hasLayerInfo = !!layerInfo || !!featureDataSource?.attributeDefinition;

          return {
            items: hasLayerInfo
              ? getDataFromRelatedFeatures({
                  config: element,
                  filters: config?.filters,
                  relatedConfig: relatedAxis,
                  dataSource: featureDataSource,
                  layerInfo:
                    layerInfo ||
                    ({ layerDefinition: { attributes: featureDataSource.attributeDefinition } } as LayerInfo),
                })
              : null,
            color: axisColor,
            layerInfo,
            attributeName,
          };
        })
        .filter(({ items }) => items === null || (items?.length && !items.every(({ value }) => value === null)));
    }

    return [{ items: getDataFromAttributes(element, attributes), layerInfo: null, attributeName: null, color: null }];
  }, [config?.dataSources, config?.filters, relatedAttributes, element, attributes, layerInfos, dataSources]);

  const [data, setData] = useState<ChartDataProps[]>(fetchedData);

  useEffect(() => {
    if (!loading && fetchedData?.length) {
      setData(fetchedData);
    }
  }, [loading, fetchedData]);

  return { data, loading };
};
